/**
 * Small React hooks of the onboarding feature (W2-14 onboarding-wizard): translation through `services.i18n`,
 * the live user settings, the reduced-motion preference and the live open document (for the tour's checks).
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { I18n } from '../../i18n/types';
export { useSettings as useSettingsValue, useServicesStore as useStore } from '../../app/registry/react';

export interface ServicesText {
  readonly locale: Locale;
  readonly t: I18n['t'];
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n`, re-rendering on locale changes. */
export function useServicesText(services: Pick<AppServices, 'i18n'>): ServicesText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(() => ({ locale, t: (key, params) => i18n.t(key, params), tr: (r) => i18n.tr(r) }), [i18n, locale]);
}

/** True when animations should be avoided (Settings > General > Reduce motion, or the OS setting). */
export function prefersReducedMotion(settings: UserSettings): boolean {
  const pref = settings.general.reducedMotion;
  if (pref === 'on') return true;
  if (pref === 'off') return false;
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
